import Phaser from 'phaser';
import { PALETTE } from '../config';
import { createPortrait, type AthleteView } from '../game/AthleteView';
import { AudioEngine } from '../game/AudioEngine';
import { findAthlete } from '../game/data/athletes';
import { countryFlag } from '../game/data/flags';
import { getDiscipline } from '../game/data/disciplines';
import { setArenaPlay } from '../game/arenaContext';
import { Ads } from '../game/ads';
import { t } from '../game/i18n';
import { formatMetric } from '../game/data/metricFormat';
import { SaveManager } from '../game/SaveManager';
import type { DisciplineResult } from '../game/types';
import { drawGradientBackground } from '../ui/Background';
import { Button } from '../ui/Button';
import { drawPanel } from '../ui/Panel';
import { viewport } from '../ui/viewport';

const MEDAL = ['🥇', '🥈', '🥉'];

/** Post-race: finishing position, full standings vs the AI field, reward, replay loop. */
export class Results extends Phaser.Scene {
  private rig?: AthleteView;

  constructor() {
    super('Results');
  }

  create(result: DisciplineResult): void {
    const { w, h, cx, s } = viewport(this);
    drawGradientBackground(this);

    const groundH = Math.max(80, h * 0.14);
    this.add.graphics().fillStyle(PALETTE.track, 1).fillRect(0, h - groundH, w, groundH)
      .fillStyle(PALETTE.trackLine, 1).fillRect(0, h - groundH, w, 5);

    const place = result.rank;
    const dnf = !!result.dnf;
    const title = dnf ? t('res.abandoned') : place === 1 ? t('res.win') : t('res.place', { n: place });
    this.add.text(cx, h * 0.07, title, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round((dnf ? 38 : 46) * s)}px`, color: dnf ? '#9fb0e0' : place === 1 ? '#ffd24a' : '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5);

    // Podium celebration: confetti rains for a top-3 finish (a burst for the win).
    if (!dnf && place <= 3) this.celebrate(w, place === 1);
    // New personal record gets its own celebratory banner (even off the podium).
    if (!dnf && result.newRecord) this.recordBanner(w, h, cx, s);

    const athlete = findAthlete([...SaveManager.instance.roster], result.athleteId);
    if (athlete) {
      const displayH = Phaser.Math.Clamp(h * 0.4, 180, 340);
      this.rig = createPortrait(this, w * 0.82, h - groundH + 4, athlete, displayH);
      this.rig.setPose(!dnf && place === 1 ? 'celebrate' : 'exhausted');
    }

    // Show an interstitial on every finished race — win OR lose (GameMonetize placement rule).
    // Skipped for an abandoned race (DNF). A short delay lets the result register first; the SDK
    // frequency-caps actual displays, so calling it each race-end is safe.
    if (!dnf) this.time.delayedCall(700, () => Ads.interstitial());

    this.standings(result, w, h, s);

    const coin = this.add.text(w * 0.3, h * 0.78, result.reward.coins > 0 ? `🪙 +${result.reward.coins}` : '', {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(30 * s)}px`, color: '#ffd24a', fontStyle: 'bold',
    }).setOrigin(0.5).setScale(0);
    this.tweens.add({ targets: coin, scale: 1, duration: 500, delay: 300, ease: 'Back.out' });

    const by = h - groundH / 2;
    const bh = Math.max(58, 66 * s);
    const fs2 = Math.round(20 * s);
    if (result.arena) {
      // Tournament race: focus the player back on the tournament (replay re-counts towards it).
      const arena = result.arena;
      const bw = Math.min(230, w * 0.3);
      const gap = bw + Math.max(8, 12 * s);
      const xs = [cx - gap, cx, cx + gap];
      new Button(this, xs[0]!, by, `🏆 ${t('res.backToTournament')}`, () => this.scene.start('ArenaDetail', { tournamentId: arena.tournamentId }), {
        width: bw, height: bh, color: PALETTE.laneAccent, fontSize: Math.round(18 * s),
      });
      new Button(this, xs[1]!, by, t('res.replay'), () => {
        setArenaPlay({ tournamentId: arena.tournamentId, tournamentName: arena.tournamentName, disciplineId: result.disciplineId });
        this.scene.start(getDiscipline(result.disciplineId).sceneKey, { athleteId: result.athleteId });
      }, { width: bw, height: bh, color: PALETTE.cadence, fontSize: fs2 });
      new Button(this, xs[2]!, by, t('res.menu'), () => this.scene.start('MainMenu'), {
        width: bw, height: bh, fontSize: fs2,
      });
      return;
    }
    // Four actions in one row: replay same athlete, switch animal (same event), board, menu.
    const bw = Math.min(190, w * 0.235);
    const gap = bw + Math.max(8, 12 * s);
    const xs = [cx - gap * 1.5, cx - gap * 0.5, cx + gap * 0.5, cx + gap * 1.5];
    new Button(this, xs[0]!, by, t('res.replay'), () => this.scene.start(getDiscipline(result.disciplineId).sceneKey, { athleteId: result.athleteId }), {
      width: bw, height: bh, color: PALETTE.cadence, fontSize: fs2,
    });
    new Button(this, xs[1]!, by, t('res.changeAnimal'), () => this.scene.start('RosterSelect', { disciplineId: result.disciplineId }), {
      width: bw, height: bh, color: PALETTE.good, fontSize: fs2,
    });
    new Button(this, xs[2]!, by, t('res.leaderboard'), () => this.scene.start('Leaderboard', { disciplineId: result.disciplineId }), {
      width: bw, height: bh, color: PALETTE.laneAccent, fontSize: fs2,
    });
    new Button(this, xs[3]!, by, t('res.menu'), () => this.scene.start('MainMenu'), {
      width: bw, height: bh, fontSize: fs2,
    });
  }

  private standings(result: DisciplineResult, w: number, h: number, s: number): void {
    const panelCx = w * 0.34;
    const panelW = Math.min(560, w * 0.62);
    const left = panelCx - panelW / 2 + 22 * s;
    const right = panelCx + panelW / 2 - 22 * s;
    const startY = h * 0.26;
    const rowH = Math.min(40, (h * 0.42) / Math.max(result.standings.length, 1));
    const fs = Math.round(Math.max(18, 19 * s));
    drawPanel(this, panelCx, startY + (result.standings.length * rowH) / 2 - rowH * 0.2, panelW, result.standings.length * rowH + 60 * s, { radius: 16, fillAlpha: 0.7 });
    this.add.text(left, startY - rowH * 0.8, t('res.raceStandings'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(18 * s)}px`, color: '#cfd8f5', fontStyle: 'bold' }).setOrigin(0, 0.5);
    result.standings.forEach((st, i) => {
      const y = startY + i * rowH + rowH * 0.4;
      const pos = MEDAL[i] ?? `${i + 1}`;
      const color = st.isPlayer ? '#ffd24a' : '#f4f7ff';
      const label = `${countryFlag(st.nationality)} ${st.isPlayer ? `${st.name} (${t('common.you')})` : st.name}`;
      if (st.isPlayer) this.add.graphics().fillStyle(PALETTE.laneAccent, 0.2).fillRoundedRect(left - 12, y - rowH / 2 + 2, right - left + 24, rowH - 4, 8);
      this.add.text(left, y, `${pos}  ${label}`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, color, fontStyle: st.isPlayer ? 'bold' : 'normal' }).setOrigin(0, 0.5);
      const valTxt = result.dnf && st.isPlayer ? 'DNF' : formatMetric(result.disciplineId, result.metricKind, st.metric);
      this.add.text(right, y, valTxt, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, color: result.dnf && st.isPlayer ? '#ff5d6c' : '#59d2ff', fontStyle: 'bold' }).setOrigin(1, 0.5);
    });
  }

  /** Celebratory "NEW RECORD" banner: a gold pill that pops in with a confetti burst + chime. */
  private recordBanner(w: number, h: number, cx: number, s: number): void {
    const y = h * 0.17;
    const pillW = Math.min(420, w * 0.6);
    const pillH = Math.max(44, 52 * s);
    const g = this.add.graphics().setDepth(60);
    g.fillStyle(PALETTE.sun, 1).fillRoundedRect(cx - pillW / 2, y - pillH / 2, pillW, pillH, pillH / 2);
    g.lineStyle(3, PALETTE.white, 0.6).strokeRoundedRect(cx - pillW / 2, y - pillH / 2, pillW, pillH, pillH / 2);
    const label = this.add.text(cx, y, t('res.worldRecord'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(20, Math.round(26 * s))}px`, color: '#0b1020', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(61);
    const group = [g, label];
    group.forEach((o) => o.setScrollFactor(0).setScale(0));
    this.tweens.add({ targets: group, scale: 1, duration: 480, delay: 250, ease: 'Back.out' });
    this.tweens.add({ targets: label, angle: { from: -3, to: 3 }, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.inOut', delay: 730 });
    // Gold burst behind the banner.
    this.time.delayedCall(250, () => {
      this.add.particles(cx, y, 'confetti', {
        lifespan: 1600, speed: { min: 180, max: 420 }, angle: { min: 0, max: 360 },
        scale: { start: 1.1, end: 0.3 }, alpha: { start: 1, end: 0 }, gravityY: 360,
        tint: [0xffd24a, 0xfff4c2, 0xffffff], emitting: false,
      }).setScrollFactor(0).setDepth(59).explode(40);
      AudioEngine.chime();
    });
  }

  /** Confetti from the top of the screen; a denser burst for a win. */
  private celebrate(w: number, win: boolean): void {
    const tints = [0xff7a59, 0x59d2ff, 0xffd24a, 0x4ade80, 0xf4f7ff];
    const em = this.add.particles(0, -20, 'confetti', {
      x: { min: 0, max: w }, y: -20,
      lifespan: 2600, speedY: { min: 90, max: 220 }, speedX: { min: -60, max: 60 },
      angle: { min: 0, max: 360 }, rotate: { min: 0, max: 360 },
      scale: { start: 1, end: 0.6 }, alpha: { start: 1, end: 0.5 },
      gravityY: 90, frequency: win ? 24 : 70, tint: tints,
    }).setDepth(50).setScrollFactor(0);
    if (win) {
      this.add.particles(w / 2, 0, 'confetti', {
        y: 0, lifespan: 1800, speed: { min: 200, max: 460 }, angle: { min: 250, max: 290 },
        scale: { start: 1.1, end: 0.4 }, alpha: { start: 1, end: 0 }, gravityY: 420, tint: tints, emitting: false,
      }).setDepth(50).setScrollFactor(0).explode(60);
    }
    // Ease off so it doesn't run forever.
    this.time.delayedCall(win ? 1800 : 1200, () => em.stop());
    this.time.delayedCall(4600, () => em.destroy());
  }

  update(_t: number, delta: number): void {
    this.rig?.update(delta / 1000);
  }
}
